// artifacts-report-lookup (report@3): שדה "בחירה עם חיפוש" בטופס הפרמטרים (11 §1). קלט combobox + רשימת listbox; ההתנהגות
// (השהיה 250ms, q:"" בפוקוס פעם אחת, ↑/↓/Enter/Esc, המצבים) ב-LookupSearch (lib/lookup.ts). הערך שנשלח הוא ה-value של השורה
// שנבחרה; הקלדה מבטלת את הבחירה (טקסט בלי בחירה — "בחר מהרשימה" באימות הטופס). המצב בשורה מתחת לשדה, בלי מודאל.
import { useEffect, useId, useReducer, useRef } from 'react';
import type { LookupResponse } from '../lib/contract.ts';
import { LookupSearch, type LookupRow } from '../lib/lookup.ts';
import { lookupKeys, type Field, type Values } from '../lib/schemaForm.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { Input } from './ui/input.tsx';

export type LookupFn = (param: string, q: string) => Promise<LookupResponse>;

export function LookupField({
  f, id, values, set, lookup, invalid, describedBy,
}: {
  f: Extract<Field, { kind: 'lookup' }>;
  id: string;
  values: Values;
  set: (k: string, v: string) => void;
  lookup: LookupFn;
  invalid?: boolean;
  describedBy?: string;
}) {
  const k = lookupKeys(f);
  const listId = useId();
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const fetchRef = useRef(lookup);
  fetchRef.current = lookup;
  const search = useRef<LookupSearch | null>(null);
  search.current ??= new LookupSearch((q) => fetchRef.current(f.id, q), rerender);
  const s = search.current;
  useEffect(() => () => s.dispose(), [s]);

  const pick = (r: LookupRow) => {
    set(f.id, r.labelHe);
    set(k.value, String(r.value));
    set(k.label, r.labelHe);
  };
  const text = values[f.id] ?? '';
  const expanded = s.open && s.rows.length > 0;
  const optionId = (i: number) => `${listId}-o${i}`;
  return (
    <div className="flex flex-col gap-1" data-testid={`lookup-${f.id}`}>
      {/* הרשימה צמודה לשדה עצמו (לא מתחת לשורת המצב) */}
      <div className="relative">
        <Input
          id={id}
          name={f.id}
          type="text"
          dir="auto"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={expanded && s.active >= 0 ? optionId(s.active) : undefined}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          placeholder={T.form.searchPlaceholder}
          value={text}
          onChange={(e) => {
            set(f.id, e.target.value);
            // הקלדה = אין בחירה, עד שבוחרים שורה
            if (values[k.value]) {
              set(k.value, '');
              set(k.label, '');
            }
            s.type(e.target.value);
          }}
          onKeyDown={(e) => {
            const r = s.key(e.key);
            if (r === null) return;
            // Enter ברשימה פתוחה = בחירה, לא שליחת הטופס
            e.preventDefault();
            if (r !== 'handled') pick(r);
          }}
          onFocus={() => s.focus(text, !!values[k.value])}
          onBlur={() => s.close()}
          data-testid={`lookup-${f.id}-input`}
        />
        <ul
          id={listId}
          role="listbox"
          aria-label={f.label}
          hidden={!expanded}
          className="absolute inset-x-0 top-full z-20 m-0 mt-1 max-h-64 list-none overflow-y-auto rounded-sm border border-border bg-card p-1 shadow-lg"
        >
          {s.rows.map((r, i) => (
            <li
              key={`${r.value}-${i}`}
              id={optionId(i)}
              role="option"
              aria-selected={i === s.active}
              // mousedown — לפני ה-blur של הקלט (אחרת הרשימה נסגרת לפני הלחיצה)
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s.pick(i));
              }}
              onMouseEnter={() => s.hover(i)}
              className={cn('flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-sm px-3 py-2', i === s.active && 'bg-primary-soft')}
              data-testid="lookup-option"
            >
              <bdi dir="auto" className="min-w-0 truncate">
                {r.labelHe}
              </bdi>
              {r.hintHe && (
                <bdi dir="auto" className="shrink-0 text-sm text-muted-foreground">
                  {r.hintHe}
                </bdi>
              )}
            </li>
          ))}
        </ul>
      </div>
      {s.status && (
        <p role="status" className={cn('m-0 text-sm', s.status.kind === 'error' ? 'text-error' : 'text-muted-foreground')} data-testid={`lookup-${f.id}-status`}>
          {s.status.textHe}
        </p>
      )}
    </div>
  );
}
